import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '~/store/authStore.js';
import { useSessionStore } from '~/store/sessionStore.js';

/**
 * Session-driven navigation, written once.
 *
 * Five screens carried the signed-out redirect as their own effect, with two
 * spellings of the URL encoding: runtime `encodeURIComponent` and hand-typed
 * `%2F` literals. The spellings produce identical URLs, which is exactly the
 * state a contract is in the day before somebody edits one copy. This file is
 * the one place the `/auth?next=` contract is built.
 */

/**
 * Where a player with no session goes to sign in, and comes back to `next`
 * after. `register` opens the form on creating an account instead.
 */
export function signInHref(
  next: string,
  { register = false }: { readonly register?: boolean } = {},
): string {
  return `/auth?${register ? 'mode=register&' : ''}next=${encodeURIComponent(next)}`;
}

/**
 * Hold a screen that needs a session, and send a player with none to `/auth`,
 * carrying `next` so signing in lands them back where they were headed.
 *
 * A session, not an account: online play is open to anonymous players, and
 * every device gets one of its own on first launch. The only way to reach the
 * redirect is for that registration to have failed, which means no network on a
 * first launch, and signing in is then the one thing left that could produce a
 * session.
 *
 * `ready` is what makes this safe. Without it the first render of a fresh
 * install has no token yet, and every guarded screen would bounce to `/auth`
 * in the second before the device registers. Pass `next` as `null` while the
 * destination is unknown (a route param still resolving) to hold the redirect.
 */
export function useRequireSession(next: string | null): void {
  const token = useAuthStore((s) => s.token);
  const ready = useAuthStore((s) => s.ready);
  const navigate = useNavigate();
  useEffect(() => {
    if (ready && token === null && next !== null) {
      void navigate(signInHref(next), { replace: true });
    }
  }, [ready, token, next, navigate]);
}

/** The routes where the player is already seated at a table of some kind. */
const AT_A_TABLE = ['/game', '/local', '/lobby'];

/**
 * Enters a table that formed as the player left the queue, from wherever they
 * went. Mounted once, at the app's root, because the queue screen that found
 * the table is gone by the time the server says so.
 */
export function StrayTableDoor(): null {
  const gameId = useSessionStore((s) => s.strayGameId);
  const signingOut = useAuthStore((s) => s.signingOut);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  useEffect(() => {
    /* A sign-out still in flight keeps the old token until it lands: wait for
       it, so the owner is compared with the session that results. */
    if (gameId === null || signingOut) return;
    const owner = useSessionStore.getState().strayFor;
    useSessionStore.getState().strayTableTaken();
    // Found for a session that has since signed out: it is that player's table.
    if (owner !== useAuthStore.getState().token) return;
    /* A player already at a table (an offline game, a private table, another
       game) is not pulled away from it: one table at a time. Pushed, not
       replaced: Back from the game returns to where they went. */
    if (AT_A_TABLE.some((root) => pathname === root || pathname.startsWith(`${root}/`))) return;
    void navigate(`/game/${gameId}`);
  }, [gameId, signingOut, navigate, pathname]);
  return null;
}

/** Enter the game a lobby or queue just matched us into, exactly once. */
export function useEnterMatchedGame(): void {
  const gameId = useSessionStore((s) => s.gameId);
  const navigate = useNavigate();
  useEffect(() => {
    if (gameId === null) return;
    void navigate(`/game/${gameId}`, { replace: true });
    // Leave nothing behind: coming back to /play should offer a fresh queue,
    // not re-navigate into the game we just left.
    useSessionStore.getState().reset();
  }, [gameId, navigate]);
}
